import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Phone,
  PhoneSlash as PhoneMissed,
  PhoneDisconnect as PhoneOff,
  ArrowLeft,
  CircleNotch,
  CheckCircle,
  XCircle,
  Warning,
  PaperPlaneTilt,
  Clock,
  ChartBar,
  CopySimple,
  Megaphone,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";

const RANGES = [
  { value: 1, label: "Today" },
  { value: 7, label: "Last 7d" },
  { value: 30, label: "Last 30d" },
  { value: 90, label: "Last 90d" },
];

const FILTERS = [
  { value: "all", label: "All missed" },
  { value: "yes", label: "Followup sent" },
  { value: "no", label: "Followup skipped" },
];

function StatCard({ icon: Icon, label, value, hint, tint = "indigo" }) {
  const tintMap = {
    indigo: "bg-indigo-500/10 text-indigo-300 border-indigo-500/20",
    rose: "bg-rose-500/10 text-rose-300 border-rose-500/20",
    emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/20",
    sky: "bg-sky-500/10 text-sky-300 border-sky-500/20",
  };
  return (
    <div
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-xl border p-4 ${tintMap[tint] || tintMap.indigo} backdrop-blur-md`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        <Icon size={16} weight="duotone" />
        {label}
      </div>
      <div className="mt-2 text-3xl font-bold tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-[11px] opacity-60">{hint}</div>}
    </div>
  );
}

function StatusBadge({ row }) {
  if (row.followup_sent) {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
        <CheckCircle size={12} weight="fill" className="mr-1" />
        Sent
      </Badge>
    );
  }
  const reason = row.followup_skipped_reason || "";
  if (reason.startsWith("cooldown")) {
    return (
      <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">
        <Clock size={12} weight="fill" className="mr-1" />
        Cooldown
      </Badge>
    );
  }
  if (reason === "no_caller_number") {
    return (
      <Badge className="bg-zinc-500/15 text-zinc-300 border-zinc-500/30">
        <PhoneOff size={12} weight="fill" className="mr-1" />
        No phone
      </Badge>
    );
  }
  if (row.followup_error) {
    return (
      <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30">
        <XCircle size={12} weight="fill" className="mr-1" />
        Failed
      </Badge>
    );
  }
  return (
    <Badge className="bg-zinc-500/15 text-zinc-300 border-zinc-500/30">
      <Warning size={12} weight="fill" className="mr-1" />
      Skipped
    </Badge>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) +
      " " +
      d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return iso;
  }
}

function MissedCalls() {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  // Per user rule 2026-02-13 — managment_feedback send-history list.
  const [feedbackSends, setFeedbackSends] = useState([]);
  // Per user rule 2026-02-13 — section near the top of the page is
  // collapsible so it doesn't crowd the page while staying accessible
  // without scrolling to the bottom.
  const [mfHistoryOpen, setMfHistoryOpen] = useState(false);
  // Per user rule 2026-02-14 — outbound (click-to-call) audit list.
  // Shows which agent clicked Call on which customer + how long the
  // bridged call lasted (duration_seconds from the Tata hangup webhook).
  const [outboundCalls, setOutboundCalls] = useState([]);
  const [outboundOpen, setOutboundOpen] = useState(false);

  // Defensive: any value coming from the API (or legacy DB rows) might be
  // a dict — JSX cannot render dicts (React error #31). Coerce to a clean
  // string before display.
  const safeStr = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (typeof v === "object") {
      return (
        v.name ||
        v.full_name ||
        v.display_name ||
        v.number ||
        v.agent_number ||
        v.id ||
        ""
      );
    }
    return String(v);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    // eslint-disable-next-line no-console
    console.log("[MissedCalls] loadAll start", { days, filter });
    try {
      const [s, l, c, fb, ob] = await Promise.all([
        api.get(`/tata/missed-calls/stats?days=${days}`),
        api.get(`/tata/missed-calls?days=${days}&sent=${filter}&limit=200`),
        api.get(`/tata/config`),
        api.get(`/templates/managment-feedback/history?limit=100`)
          .catch(() => ({ data: { items: [] } })),
        api.get(`/tata/outbound-calls/history?days=${days}&limit=200`)
          .catch(() => ({ data: { items: [] } })),
      ]);
      // eslint-disable-next-line no-console
      console.log("[MissedCalls] api ok", {
        statsKeys: Object.keys(s.data || {}),
        itemsLen: (l.data?.items || []).length,
        configKeys: Object.keys(c.data || {}),
        feedbackLen: (fb.data?.items || []).length,
        outboundLen: (ob.data?.items || []).length,
      });
      setStats(s.data);
      setItems(Array.isArray(l.data?.items) ? l.data.items : []);
      setConfig(c.data);
      setFeedbackSends(Array.isArray(fb.data?.items) ? fb.data.items : []);
      setOutboundCalls(Array.isArray(ob.data?.items) ? ob.data.items : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[MissedCalls] api error", err);
      toast.error(err?.response?.data?.detail || "Failed to load missed-call data");
    } finally {
      setLoading(false);
    }
  }, [days, filter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const totals = stats?.totals || {};
  const daily = (stats?.daily || []).filter((d) => d && typeof d.day === "string" && d.day.length >= 10);

  const peakDay = useMemo(() => {
    if (!daily.length) return null;
    return daily.reduce((a, b) => ((b.missed || 0) > (a.missed || 0) ? b : a), daily[0]);
  }, [daily]);

  const copyWebhookUrl = () => {
    const url = `${window.location.origin}/api/webhook/tata-cloud-phone`;
    navigator.clipboard.writeText(url);
    toast.success("Webhook URL copied");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0d18] via-[#11142a] to-[#0a0c1a] text-[var(--fg)]">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-dashboard"
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <PhoneMissed size={28} weight="duotone" className="text-rose-400" />
                Missed Calls → WhatsApp
              </h1>
              <p className="text-sm text-zinc-400">
                Tata Cloud Phone webhook auto-sends a follow-up template to every missed caller
              </p>
            </div>
          </div>
          <Button
            data-testid="refresh-missed-calls"
            onClick={loadAll}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-500"
          >
            {loading ? <CircleNotch size={16} className="animate-spin" /> : "Refresh"}
          </Button>
        </div>

        {/* Range selector */}
        <div className="flex items-center gap-2 flex-wrap">
          {RANGES.map((r) => (
            <Button
              key={r.value}
              data-testid={`range-${r.value}`}
              variant={days === r.value ? "default" : "outline"}
              size="sm"
              onClick={() => setDays(r.value)}
              className={
                days === r.value
                  ? "bg-indigo-600 hover:bg-indigo-500"
                  : "bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              }
            >
              {r.label}
            </Button>
          ))}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard
            icon={PhoneMissed}
            label="Missed Calls"
            value={(totals.missed_calls ?? 0).toLocaleString()}
            hint={`out of ${totals.events_received ?? 0} call events`}
            tint="rose"
          />
          <StatCard
            icon={Phone}
            label="Answered Calls"
            value={(totals.answered_calls ?? 0).toLocaleString()}
            hint={`${totals.answer_rate_pct ?? 0}% of ${totals.events_received ?? 0} call events`}
            tint="emerald"
          />
          <StatCard
            icon={PaperPlaneTilt}
            label="Followups Sent"
            value={(totals.followups_sent ?? 0).toLocaleString()}
            hint={`${totals.send_rate_pct ?? 0}% of missed calls`}
            tint="sky"
          />
          <StatCard
            icon={Clock}
            label="Cooldown Skipped"
            value={(totals.cooldown_skipped ?? 0).toLocaleString()}
            hint={`Same number, within ${config?.cooldown_minutes ?? 30}min`}
            tint="amber"
          />
          <StatCard
            icon={XCircle}
            label="Send Failed"
            value={(totals.send_failed ?? 0).toLocaleString()}
            hint="Meta API rejection / config error"
            tint={totals.send_failed > 0 ? "rose" : "sky"}
          />
        </div>

        {/* Management Feedback — Send history (top-of-page, collapsible).
            Per user rule 2026-02-13 — "isko bhi upar list me dikha
            dijiye, itna niche kyu jayega dekhne". Default collapsed
            so it doesn't push the missed-calls table down. Click the
            header to expand. */}
        <div
          className="rounded-xl border border-violet-500/30 bg-violet-500/[0.06] backdrop-blur-md overflow-hidden"
          data-testid="mf-history-card"
        >
          <button
            type="button"
            onClick={() => setMfHistoryOpen((v) => !v)}
            className="w-full px-5 py-3 flex items-center gap-2 hover:bg-violet-500/10 transition-colors text-left"
            data-testid="mf-history-toggle"
          >
            {mfHistoryOpen ? (
              <CaretDown size={14} weight="bold" className="text-violet-300" />
            ) : (
              <CaretRight size={14} weight="bold" className="text-violet-300" />
            )}
            <Megaphone size={16} weight="duotone" className="text-violet-300" />
            <h2 className="text-sm font-semibold tracking-wide text-zinc-100">
              Management Feedback — Send history
            </h2>
            <Badge className="ml-auto bg-violet-500/15 text-violet-200 border-violet-500/30">
              {feedbackSends.length} sent
            </Badge>
          </button>
          {mfHistoryOpen && (
            <div className="border-t border-violet-500/20">
              {feedbackSends.length === 0 ? (
                <div className="p-6 text-sm text-white/60 text-center">
                  No <code className="font-mono text-xs">managment_feedback</code> templates sent yet.
                  Open any chat → click the purple <em>Feedback</em> button in the header to send one.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-white/60">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium">When (IST)</th>
                        <th className="text-left px-4 py-2.5 font-medium">Customer</th>
                        <th className="text-left px-4 py-2.5 font-medium">Sent by</th>
                        <th className="text-left px-4 py-2.5 font-medium">Variables</th>
                        <th className="text-left px-4 py-2.5 font-medium">wamid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbackSends.map((row, idx) => {
                        const sentAt = row.sent_at
                          ? new Date(row.sent_at).toLocaleString("en-IN", {
                              dateStyle: "medium",
                              timeStyle: "short",
                              timeZone: "Asia/Kolkata",
                            })
                          : "—";
                        const cust = row.customer_name
                          ? `${row.customer_name}  ·  +${row.phone}`
                          : `+${row.phone}`;
                        return (
                          <tr
                            key={`${row.wamid || idx}-${row.sent_at || idx}`}
                            className="border-t border-white/5 hover:bg-white/[0.02]"
                            data-testid={`mf-history-row-${idx}`}
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-white/80">
                              {sentAt}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => navigate(`/?phone=${row.phone}`)}
                                className="text-violet-300 hover:underline text-left"
                                title="Open conversation"
                              >
                                {cust}
                              </button>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-white/80">
                              {row.sent_by_name || "Unknown"}
                            </td>
                            <td className="px-4 py-2 text-white/70">
                              {(row.body_params || []).join(" · ") || "—"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-[10px] font-mono text-white/40">
                              {row.wamid
                                ? row.wamid.slice(0, 12) + "…"
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Outbound (click-to-call) history — collapsible.
            Per user rule 2026-02-14 — admin needs to audit kis agent ne
            kis customer pe Call button click kiya, aur Tata bridge call
            kitna der chala. Joined with `tata_call_events` for talk-time. */}
        <div
          data-testid="outbound-calls-section"
          className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 backdrop-blur-md overflow-hidden"
        >
          <button
            type="button"
            onClick={() => setOutboundOpen((v) => !v)}
            className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-emerald-500/10 transition-colors"
            data-testid="outbound-calls-toggle"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-200">
              {outboundOpen ? (
                <CaretDown size={14} weight="bold" />
              ) : (
                <CaretRight size={14} weight="bold" />
              )}
              <Phone size={16} weight="duotone" className="text-emerald-300" />
              Outbound calls — Click-to-call history
            </div>
            <span className="text-xs text-emerald-300/80 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">
              {outboundCalls.length} clicks
            </span>
          </button>
          {outboundOpen && (
            <div className="border-t border-emerald-500/20">
              {outboundCalls.length === 0 ? (
                <div className="px-4 py-6 text-sm text-zinc-400 text-center">
                  No outbound calls in this window.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-white/5">
                        <th className="px-4 py-2 font-medium">WHEN (IST)</th>
                        <th className="px-4 py-2 font-medium">CUSTOMER</th>
                        <th className="px-4 py-2 font-medium">AGENT</th>
                        <th className="px-4 py-2 font-medium">RESULT</th>
                        <th className="px-4 py-2 font-medium">TALK TIME</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outboundCalls.map((row, idx) => {
                        const clickedAt = row.timestamp
                          ? new Date(row.timestamp).toLocaleString("en-IN", {
                              dateStyle: "medium",
                              timeStyle: "short",
                              timeZone: "Asia/Kolkata",
                            })
                          : "—";
                        const dur = Number(row.duration_seconds || 0);
                        const durLabel = dur > 0
                          ? `${Math.floor(dur / 60)}m ${dur % 60}s`
                          : null;
                        return (
                          <tr
                            key={`${row.tata_uuid || row.timestamp}-${idx}`}
                            className="border-t border-white/5 hover:bg-white/[0.02]"
                            data-testid={`outbound-row-${idx}`}
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-white/80">
                              {clickedAt}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => navigate(`/?phone=${row.phone}`)}
                                className="text-violet-300 hover:underline"
                                title="Open conversation"
                              >
                                +{row.phone}
                              </button>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-white/80">
                              {safeStr(row.agent_name) || "Unknown"}
                              {row.agent_role && row.agent_role !== "agent" && (
                                <span className="ml-1 text-[10px] text-zinc-500">
                                  ({row.agent_role})
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {row.ok ? (
                                <span className="text-emerald-300 text-[11px] inline-flex items-center gap-1">
                                  <CheckCircle size={12} weight="fill" />
                                  Dialed
                                </span>
                              ) : (
                                <span
                                  className="text-rose-300 text-[11px] inline-flex items-center gap-1"
                                  title={row.status || "unknown"}
                                >
                                  <XCircle size={12} weight="fill" />
                                  {row.status || "failed"}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-white/80">
                              {durLabel ? (
                                <span className="inline-flex items-center gap-1 text-emerald-300">
                                  <Clock size={11} weight="bold" />
                                  {durLabel}
                                </span>
                              ) : (
                                <span className="text-zinc-500">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>


        {daily.length > 0 && (
          <div
            data-testid="daily-chart"
            className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 backdrop-blur-md"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
                <ChartBar size={18} weight="duotone" className="text-indigo-400" />
                Daily breakdown
              </div>
              {peakDay && (
                <div className="text-xs text-zinc-400">
                  Peak: <span className="text-amber-300 font-semibold">{peakDay.day}</span> ·{" "}
                  {peakDay.missed} missed
                </div>
              )}
            </div>
            <div className="flex items-end gap-1 h-32">
              {daily.map((d) => {
                const max = Math.max(...daily.map((x) => x.missed), 1);
                const missedH = (d.missed / max) * 100;
                const sentH = d.missed > 0 ? (d.sent / d.missed) * missedH : 0;
                return (
                  <div
                    key={d.day}
                    className="flex-1 flex flex-col items-center justify-end group relative"
                    title={`${d.day}\nMissed: ${d.missed}\nSent: ${d.sent}`}
                  >
                    <div
                      className="w-full rounded-t bg-rose-500/30"
                      style={{ height: `${missedH}%`, minHeight: d.missed > 0 ? "4px" : "0" }}
                    />
                    {sentH > 0 && (
                      <div
                        className="w-full -mt-px bg-emerald-500/70"
                        style={{ height: `${sentH}%` }}
                      />
                    )}
                    <div className="text-[9px] text-zinc-500 mt-1 truncate w-full text-center">
                      {d.day.slice(5)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-zinc-400">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-rose-500/30" />
                Missed
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-emerald-500/70" />
                Followup sent
              </div>
            </div>
          </div>
        )}

        {/* Filter row */}
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <Button
              key={f.value}
              data-testid={`filter-${f.value}`}
              variant={filter === f.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.value)}
              className={
                filter === f.value
                  ? "bg-zinc-700 hover:bg-zinc-600"
                  : "bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              }
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Detail table */}
        <div
          data-testid="missed-calls-table"
          className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-zinc-800 text-xs uppercase tracking-wider text-zinc-400 grid grid-cols-12 gap-2">
            <div className="col-span-3">When</div>
            <div className="col-span-3">Caller</div>
            <div className="col-span-2">Agent</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Sent at</div>
          </div>
          {loading && (
            <div className="p-8 text-center text-zinc-500">
              <CircleNotch size={20} className="inline animate-spin mr-2" /> Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-zinc-500 text-sm">
              No missed calls in this window. 🎉
            </div>
          )}
          {!loading &&
            items.map((row, idx) => {
              const r = row || {};
              const agent = r.agent && typeof r.agent === "object" ? r.agent : {};
              const key = safeStr(r.event_id) || `row-${idx}`;
              const agentName = safeStr(agent.name);
              const agentDid = safeStr(agent.did);
              const customerName = safeStr(r.customer_name);
              const callerPhone = safeStr(r.caller_phone);
              const startStamp = safeStr(r.start_stamp);
              const followupError = safeStr(r.followup_error);
              return (
              <div
                key={key}
                data-testid={`missed-call-row-${key}`}
                className="px-4 py-3 border-b border-zinc-800/60 grid grid-cols-12 gap-2 text-sm hover:bg-zinc-800/30"
              >
                <div className="col-span-3 text-zinc-300">
                  {fmtTime(r.received_at)}
                  {startStamp && (
                    <div className="text-[10px] text-zinc-500">{startStamp}</div>
                  )}
                </div>
                <div className="col-span-3">
                  <div className="text-zinc-200 font-mono text-[13px]">
                    +{callerPhone || "—"}
                  </div>
                  {customerName && (
                    <div className="text-[11px] text-zinc-400">{customerName}</div>
                  )}
                </div>
                <div className="col-span-2 text-zinc-300 text-[13px]">
                  {agentName || "—"}
                  {agentDid && (
                    <div className="text-[10px] text-zinc-500">DID: {agentDid}</div>
                  )}
                </div>
                <div className="col-span-2">
                  <StatusBadge row={r} />
                  {followupError && (
                    <div className="text-[10px] text-rose-400 mt-1 truncate" title={followupError}>
                      {followupError}
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-right text-[12px] text-zinc-400">
                  {r.followup_sent_at ? fmtTime(r.followup_sent_at) : "—"}
                </div>
              </div>
              );
            })}
        </div>

        {/* Setup card */}
        {config && (
          <div
            data-testid="setup-card"
            className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4 backdrop-blur-md"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-3">
              <Phone size={18} weight="duotone" />
              Tata Tele setup
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-[13px]">
              <div className="space-y-2">
                <div className="text-zinc-400 text-xs">Webhook URL (paste in Tata Tele console)</div>
                <div className="flex items-center gap-2 bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 font-mono text-xs">
                  <span className="flex-1 truncate">
                    {window.location.origin}/api/webhook/tata-cloud-phone
                  </span>
                  <Button
                    data-testid="copy-webhook-url"
                    size="sm"
                    variant="ghost"
                    onClick={copyWebhookUrl}
                    className="h-6 px-2 text-zinc-400 hover:text-white"
                  >
                    <CopySimple size={14} />
                  </Button>
                </div>
                <div className="text-zinc-400 text-xs mt-3">Auth header</div>
                <div className="bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 font-mono text-xs">
                  {config.auth_header_name}: <span className="text-zinc-500">(set in backend .env)</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-zinc-400 text-xs">Template</div>
                <div className="bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 font-mono text-xs">
                  {config.template_name} <span className="text-zinc-500">·</span>{" "}
                  {config.template_language}
                </div>
                <div className="text-zinc-400 text-xs mt-3">Cooldown</div>
                <div className="bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 font-mono text-xs">
                  {config.cooldown_minutes} min per phone number
                </div>
                <div className="text-zinc-400 text-xs mt-3">Status</div>
                <div className="flex items-center gap-2 text-xs">
                  {config.webhook_token_configured ? (
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                      <CheckCircle size={12} weight="fill" className="mr-1" />
                      Webhook token configured
                    </Badge>
                  ) : (
                    <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30">
                      <XCircle size={12} weight="fill" className="mr-1" />
                      Token NOT set
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MissedCalls;
